'use client';

import React from 'react';
import { motion } from 'framer-motion';
import { ShieldPlus, ArrowRight, BadgeCheck } from 'lucide-react';
import { useCustomVaccines } from '@/hooks/useVaccines';
import { useTheme } from '@/providers/ThemeProvider';
import { ServiceForm } from '@/lib/Apis/models';

interface SpecialtyVaccineBannerProps {
  /** Navigate to the custom vaccine's booking flow (e.g. router.push(`/vaccines/specialty/${v.id}`)). */
  onBook: (vaccine: ServiceForm) => void;
  /** Optional copy overrides. */
  title?: string;
  subtitle?: string;
  className?: string;
}

// Home-screen banner for the pharmacy's specialty (custom-form) immunizations.
// Each one runs its own questionnaire and schedule, so every card books
// individually instead of joining the shared multi-vaccine flow. Hides itself
// when the store has none, so it's safe to drop onto any page.
export function SpecialtyVaccineBanner({
  onBook,
  title = 'Specialty immunizations',
  subtitle,
  className = '',
}: SpecialtyVaccineBannerProps) {
  const { vaccines, isLoading } = useCustomVaccines();
  const { config } = useTheme();

  if (isLoading || vaccines.length === 0) return null;

  const sub =
    subtitle ??
    `Beyond routine shots — ${config.storeName} offers tailored protection with a quick, dedicated booking for each.`;

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className={`relative overflow-hidden rounded-2xl border border-hsecondary-100 bg-gradient-to-br from-hsecondary-50 to-white p-5 shadow-sm sm:p-7 ${className}`}
    >
      {/* Decorative blob */}
      <div className="pointer-events-none absolute -right-10 -top-10 h-44 w-44 rounded-full bg-hsecondary-100/60 blur-2xl" />

      <div className="relative">
        <div className="flex items-start gap-4">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-hsecondary-500 text-white shadow-sm">
            <ShieldPlus className="h-6 w-6" />
          </div>
          <div className="min-w-0">
            <h3 className="text-lg font-bold leading-tight text-gray-900 sm:text-xl">{title}</h3>
            <p className="mt-1 text-sm text-gray-600">{sub}</p>
            <p className="mt-2 flex items-center gap-1.5 text-xs font-medium text-emerald-700">
              <BadgeCheck className="h-3.5 w-3.5" /> Free with most insurance plans
            </p>
          </div>
        </div>

        <div className="mt-5 grid gap-2.5 sm:grid-cols-2">
          {vaccines.map((v) => (
            <motion.button
              key={v.id}
              type="button"
              onClick={() => onBook(v)}
              whileHover={{ scale: 1.01 }}
              whileTap={{ scale: 0.99 }}
              className="group flex items-center justify-between gap-3 rounded-xl border border-gray-200 bg-white p-4 text-left shadow-sm transition-colors hover:border-hsecondary-400"
            >
              <div className="min-w-0">
                <p className="truncate font-medium text-gray-900">{v.name}</p>
                {v.description && <p className="truncate text-xs text-gray-500">{v.description}</p>}
              </div>
              <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-hprimary-600 px-3.5 py-1.5 text-xs font-semibold text-white shadow-sm transition-colors group-hover:bg-hprimary-700">
                Book
                <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
              </span>
            </motion.button>
          ))}
        </div>
      </div>
    </motion.div>
  );
}
